import { GridColDef } from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import { dataGridCol } from '@shared/components/grid/common';
import { formatCurrency, gridDateFormatter, gridDateTimeFormatter } from '@shared/commonFunctions';
import { NumberInput } from '@shared/inputs/NumberInput';
import { colorTheme } from '@styles/muiTheme';
import { IDealSheet, RevenueType, DSColumn } from './interfaces';

export const historyColumns: GridColDef[] = [
  dataGridCol('createdDate', {
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
    flex: 1,
    minWidth: 100,
  }),
  dataGridCol('isNegative', {
    flex: 1,
    minWidth: 100,
    renderCell: (params) => (params.row.isNegative ? <Box>'Negative'</Box> : null),
  }),
];

export const adjustmentsColumns: GridColDef[] = [
  dataGridCol('adjustmentPayOff', {
    headerName: 'Payoff Adjustment',
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('amountFinanced', {
    headerName: 'Adjustment',
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('totalAdjustment', {
    headerName: 'Total Adjustment',
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
];

export const payOffQuoteData = (data?: IDealSheet) => [
  {
    title: DSColumn.amount,
    value: formatCurrency(data?.payoffAmountActual),
    id: 1,
  },
  {
    title: DSColumn.goodThrough,
    value:
      data?.payoffGoodDateActual && gridDateFormatter({ id: 'date', field: 'date', value: data.payoffGoodDateActual, api: {} }),
    id: 2,
  },
  {
    title: DSColumn.perDiem,
    value: formatCurrency(data?.payoffPerDiemActual),
    id: 3,
  },
];

export const prodData = (data?: IDealSheet) => {
  const isMBi = data?.warrantyType?.toUpperCase() === RevenueType.MBI;
  return [
    {
      id: 1,
      type: RevenueType.GAP,
      retail: `$${data?.gapRetail}`,
      cost: data?.gapCost,
      packFee: `$${data?.gapPack}`,
      productSalesTax: `$${data?.gapTax}`,
    },
    {
      id: 2,
      type: RevenueType.VSC,
      retail: isMBi ? 0 : data?.warrantyRetail,
      cost: isMBi ? 0 : data?.warrantyCost,
      packFee: isMBi ? 0 : data?.warrantyPack,
      productSalesTax: isMBi ? 0 : data?.warrantyTax,
    },
    {
      id: 3,
      type: RevenueType.MBI,
      retail: isMBi ? data?.warrantyRetail : 0,
      cost: isMBi ? data?.warrantyCost : 0,
      packFee: isMBi ? data?.warrantyPack : 0,
      productSalesTax: isMBi ? data?.warrantyTax : 0,
    },
    {
      id: 4,
      type: RevenueType.MBA,
      retail: data?.mbaRetail,
      cost: data?.mbaCost,
      packFee: data?.mbaPack,
      productSalesTax: data?.mbaTax,
    },
    {
      id: 5,
      type: RevenueType.PAF,
      retail: data?.appearanceProtRetail,
      cost: data?.appearanceProtCost,
      packFee: data?.appearanceProtPack,
      productSalesTax: data?.appearanceProtTax,
    },
    {
      id: 6,
      type: RevenueType.COM,
      retail: data?.comRetail,
      cost: data?.comCost,
      packFee: data?.comPack,
      productSalesTax: data?.comTax,
    },
    {
      id: 7,
      type: RevenueType.VRP,
      retail: data?.vrpRetail,
      cost: data?.vrpCost,
      packFee: data?.vrpPack,
      productSalesTax: data?.vrpTax,
    },
  ];
};

export const dealSummaryData = (data?: IDealSheet) => {
  const isFlStampFeeNullOrZero = !data?.flStampFee;
  const isVsiFeeNullOrZero = !data?.vsiFee;

  const baseFees = [
    {
      id: 1,
      title: DSColumn.cashReceived,
      value: formatCurrency(data?.cashReceived),
    },
    {
      id: 2,
      title: DSColumn.totalPayoff,
      value: formatCurrency(data?.totalPayoff),
    },
    {
      id: 3,
      title: DSColumn.otherPayable,
      value: formatCurrency(data?.otherPayable),
    },
    {
      id: 4,
      title: DSColumn.totalRevenue,
      value: formatCurrency(data?.total),
      isRevenueRow: true,
    },
  ];

  const filteredFees = baseFees.filter((fee) => {
    if (fee.isRevenueRow && isFlStampFeeNullOrZero && isVsiFeeNullOrZero) {
      return false;
    }
    return true;
  });

  return filteredFees.map((fee, index) => {
    const newId = index + 1;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isRevenueRow, ...rest } = fee;

    return {
      ...rest,
      id: newId,
    };
  });
};

export const dealSummarySecData = (data?: IDealSheet) => {
  const isFlStampFeeNullOrZero = !data?.flStampFee;
  const isVsiFeeNullOrZero = !data?.vsiFee;

  const fees = [
    {
      id: 1,
      title: DSColumn.downPayment,
      value: formatCurrency(data?.downPayment),
    },
    {
      id: 2,
      title: DSColumn.costOfProduction,
      value: formatCurrency(data?.costOfProducts),
    },
  ];

  if (isFlStampFeeNullOrZero && isVsiFeeNullOrZero) {
    fees.push({
      id: 3,
      title: DSColumn.totalRevenue,
      value: formatCurrency(data?.total),
    });
  } else {
    if (!isFlStampFeeNullOrZero) {
      fees.push({
        id: fees.length + 1,
        title: DSColumn.flStampFee,
        value: formatCurrency(data?.flStampFee),
      });
    }

    if (!isVsiFeeNullOrZero) {
      fees.push({
        id: fees.length + 1,
        title: DSColumn.vsiFee,
        value: formatCurrency(data?.vsiFee),
      });
    }
  }

  return fees;
};

export const fundingDetailsData = (data?: IDealSheet) => [
  {
    id: 1,
    title: DSColumn.fundedDate,
    value: data?.fundedDate && gridDateFormatter({ id: 'date', field: 'date', value: data?.fundedDate, api: {} }),
    editable: true,
    type: 'date',
  },
  {
    id: 2,
    title: DSColumn.amountFinanced,
    value: formatCurrency(data?.cudlAmountFinanced),
  },
  {
    id: 3,
    title: DSColumn.flatReserv,
    value: formatCurrency(data?.flatParticipation),
  },
  {
    id: 4,
    title: DSColumn.contractedPayoff,
    value: formatCurrency(data?.payoff),
  },
  {
    id: 5,
    title: DSColumn.lenderFee,
    value: formatCurrency(data?.lenderFee),
  },
  {
    id: 6,
    title: DSColumn.downPayment,
    value: formatCurrency(data?.downPayment),
  },
  {
    id: 7,
    title: DSColumn.dealerTrackPayoff,
    value: formatCurrency(Number(data?.dealerTrackPayoffAmount)),
  },
  {
    id: 8,
    title: DSColumn.customerRefund,
    value: formatCurrency(Number(data?.customerRefundAmount)),
  },
  {
    id: 9,
    title: DSColumn.packDocFee,
    value: formatCurrency(data?.totalPackDocFee),
  },
];

export const otherFeeData = (data?: IDealSheet) => {
  const fees = [
    {
      id: 1,
      title: DSColumn.packTitleFee,
      value: formatCurrency(Number(data?.totalPackTitleFee)),
    },
    {
      id: 2,
      title: DSColumn.salesTax,
      value: formatCurrency(data?.stateTax),
    },
    {
      id: 3,
      title: DSColumn.registrationFee,
      value: formatCurrency(data?.registrationFee),
    },
    {
      id: 4,
      title: DSColumn.flStampFee,
      value: formatCurrency(data?.flStampFee),
      rawValue: data?.flStampFee,
    },
    {
      id: 5,
      title: DSColumn.vsiFee,
      value: formatCurrency(data?.vsiFee),
      rawValue: data?.vsiFee,
    },
    {
      id: 6,
      title: DSColumn.cashOut,
      value: formatCurrency(data?.cashOut),
    },
    {
      id: 7,
      title: DSColumn.dealerTrackFees,
      value: formatCurrency(Number(data?.acceleratedTitleTransactionCharges)),
    },
    {
      id: 8,
      title: DSColumn.dealerTrackTaxes,
      value: formatCurrency(Number(data?.acceleratedTitleTaxes)),
    },
  ];

  return fees
    .filter((fee) => {
      if (fee.title === DSColumn.flStampFee) {
        return !!fee.rawValue;
      }
      if (fee.title === DSColumn.vsiFee) {
        return !!fee.rawValue;
      }
      return true;
    })
    .map((fee, index) => {
      const newId = index + 1;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { rawValue, ...rest } = fee;
      return {
        ...rest,
        id: newId,
      };
    });
};

export const revenueData = (data?: IDealSheet) => {
  const isMBi = data?.warrantyType?.toUpperCase() === RevenueType.MBI;

  return [
    {
      id: 1,
      type: RevenueType.GAP,
      commissionable: data?.gapCommissionable,
      nonCommissionable: data?.gapPack,
      total: data?.totalGap,
    },
    {
      id: 2,
      type: RevenueType.VSC,
      commissionable: isMBi ? 0 : data?.warrantyCommissionable,
      nonCommissionable: isMBi ? 0 : data?.warrantyPack,
      total: isMBi ? 0 : data?.totalWarranty,
    },
    {
      id: 3,
      type: RevenueType.MBI,
      commissionable: isMBi ? data?.warrantyCommissionable : 0,
      nonCommissionable: isMBi ? data?.warrantyPack : 0,
      total: isMBi ? data?.totalWarranty : 0,
    },
    {
      id: 4,
      type: RevenueType.MBA,
      commissionable: data?.mbaCommissionable,
      nonCommissionable: data?.mbaNonCommissionable,
      total: data?.totalMba,
    },
    {
      id: 5,
      type: RevenueType.PAF,
      commissionable: data?.appearanceProtCommissionable,
      nonCommissionable: data?.appearanceProtPack,
      total: data?.totalAppearanceProt,
    },
    {
      id: 6,
      type: RevenueType.COM,
      commissionable: data?.comCommissionable,
      nonCommissionable: data?.comPack,
      total: data?.totalCom,
    },
    {
      id: 7,
      type: RevenueType.VRP,
      commissionable: data?.vrpCommissionable,
      nonCommissionable: data?.vrpPack,
      total: data?.totalVrp,
    },
    {
      id: 8,
      type: RevenueType.RESERVE,
      commissionable: data?.flatParticipation,
      nonCommissionable: data?.reserveNonCommissionable,
      total: data?.totalReserve,
    },
    {
      id: 9,
      type: RevenueType.ADJPAYOFF,
      commissionable: data?.adjustedPayoffCommissionable,
      nonCommissionable: data?.adjustedPayoffNonCommissionable,
      total: data?.totalAdjustedPayoff,
    },
    {
      id: 10,
      type: RevenueType.OTHERADJUST,
      commissionable: data?.otherAdjustmentsCommissionable,
      nonCommissionable: data?.otherAdjustmentsNonCommissionable,
      total: data?.totalOtherAdjustments,
    },
    {
      id: 11,
      type: RevenueType.OTHERFEES,
      commissionable: data?.otherFeesCommissionable,
      nonCommissionable: data?.otherFeesNonCommissionable,
      total: data?.totalOtherFees,
    },
    {
      id: 12,
      type: RevenueType.PACKDOC,
      commissionable: data?.packDocFeeCommissionable,
      nonCommissionable: data?.pack,
      total: data?.totalPackDocFee,
    },
    {
      id: 13,
      type: RevenueType.TOTAL,
      commissionable: data?.totalCommissionable,
      nonCommissionable: data?.totalNonCommissionable,
      total: data?.total,
    },
  ];
};

export const productInformationColumn: GridColDef[] = [
  dataGridCol('type', {
    flex: 1,
    minWidth: 80,
  }),
  dataGridCol('retail', {
    flex: 1,
    minWidth: 100,
    renderCell: (params) => (
      <NumberInput
        disabled
        allowNegative
        decimalScale={2}
        prefix="$"
        name="type"
        value={params.value}
      />
    ),
  }),
  dataGridCol('cost', {
    flex: 1,
    minWidth: 100,
    renderCell: (params) => (
      <NumberInput
        disabled
        allowNegative
        decimalScale={2}
        prefix="$"
        name="cost"
        value={params.value}
      />
    ),
  }),
  dataGridCol('packFee', {
    flex: 1,
    minWidth: 100,
    renderCell: (params) => (
      <NumberInput
        disabled
        allowNegative
        decimalScale={2}
        prefix="$"
        name="packFee"
        value={params.value}
      />
    ),
  }),
  dataGridCol('productSalesTax', {
    flex: 1,
    minWidth: 100,
    renderCell: (params) => (
      <NumberInput
        disabled
        allowNegative
        decimalScale={2}
        prefix="$"
        name="cost"
        value={params.value}
      />
    ),
  }),
].map((i) => ({ flex: 1, minwidth: 180, resizable: false, ...i }));

export const revenueDetailColumns: GridColDef[] = [
  dataGridCol('type'),
  dataGridCol('commissionable', {
    renderCell: (params) => (
      <NumberInput
        disabled
        allowNegative
        decimalScale={2}
        prefix="$"
        name="commissionable"
        value={params.value}
      />
    ),
  }),
  dataGridCol('nonCommissionable', {
    renderCell: (params) => (
      <NumberInput
        disabled
        allowNegative
        decimalScale={2}
        prefix="$"
        name="nonCommissionable"
        value={params.value}
      />
    ),
  }),
  dataGridCol('total', {
    renderCell: (params) => (
      <NumberInput
        disabled
        allowNegative
        name="total"
        prefix="$"
        decimalScale={2}
        value={params.value}
        style={{
          background: `${colorTheme.palette.common.white}`,
          color: `${colorTheme.palette.common.black}`,
          fontWeight: 'bold',
        }}
      />
    ),
  }),
].map((column) => ({ width: 180, flex: 1, resizable: false, sortable: false, ...column }));
